import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchSharedSummary, sharedPdfUrl } from "../lib/api";
import { formatCurrency, ELECTION_TYPES, GOVERNING_REGULATIONS } from "../lib/costEngine";
import { SMARTBALLOT_LOGO } from "../assets/smartBallotLogo";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Loader2, FileText, Feather, MapPin, Users, AlertCircle } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
  } catch (e) {
    return iso;
  }
};

export default function SharedProposalPage() {
  const { token } = useParams();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchSharedSummary(token);
        setSummary(data);
      } catch (e) {
        setError(e.response?.data?.detail || "This proposal link is not available.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex items-center gap-2 text-muted-foreground" data-testid="shared-loading">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading proposal…
        </div>
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <Card className="max-w-md w-full" data-testid="shared-error">
          <CardContent className="p-10 text-center">
            <AlertCircle className="h-8 w-8 mx-auto mb-3 text-destructive" />
            <p className="font-display font-700 text-lg text-foreground">Proposal unavailable</p>
            <p className="text-sm text-muted-foreground mt-1">{error || "This proposal link is not available."}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const typeLabel = ELECTION_TYPES[summary.electionType]?.label || summary.electionType || "—";
  const items = summary.items || [];
  const fixedItems = items.filter((i) => i.section === "fixed");
  const variableItems = items.filter((i) => i.section !== "fixed");

  const renderRows = (rows) =>
    rows.map((it, i) => (
      <TableRow key={`${it.name}-${i}`}>
        <TableCell className="font-medium">{it.name}</TableCell>
        <TableCell className="text-right tabular-nums">
          {it.amount == null ? <span className="text-xs text-amber-600">Actual + 15%</span> : formatCurrency(it.amount)}
        </TableCell>
      </TableRow>
    ));

  return (
    <div className="min-h-screen bg-background" data-testid="shared-proposal-page">
      <header className="bg-white border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-10">
          <img src={SMARTBALLOT_LOGO} alt="SmartBallot" className="h-10 object-contain mb-4" />
          <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-muted-foreground">
            <Feather className="h-4 w-4" /> SmartBallot
          </div>
          <h1 className="font-display font-800 text-3xl sm:text-4xl mt-3 tracking-tight text-foreground">
            {summary.eventTitle || "Voting Event Proposal"}
          </h1>
          <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mt-3">
            <span>{typeLabel}</span>
            {summary.location && (
              <>
                <span>·</span>
                <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {summary.location}</span>
              </>
            )}
            {summary.membershipSize != null && (
              <>
                <span>·</span>
                <span className="inline-flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" /> {Number(summary.membershipSize).toLocaleString()} members
                </span>
              </>
            )}
          </div>
          {summary.clientOrg && (
            <p className="text-sm text-muted-foreground mt-2">Prepared for {summary.clientOrg}</p>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-6">
        <Card>
          <CardContent className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
            <Meta label="Governing regulations" value={GOVERNING_REGULATIONS[summary.governingRegulation] || "—"} />
            <Meta label="Prepared" value={fmtDate(summary.createdAt)} />
            <Meta label="Election type" value={typeLabel} />
            <Meta
              label="Date range"
              value={[summary.startDate, summary.endDate].filter(Boolean).map(fmtDate).join(" – ") || "To be confirmed"}
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <div className="px-4 py-2.5 border-b border-border bg-accent/40">
              <p className="font-display font-600 text-sm text-foreground">Fixed Budget Estimate</p>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Line Item</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {renderRows(fixedItems)}
                <TableRow className="bg-muted/40">
                  <TableCell className="text-right font-medium">Fixed Subtotal</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">
                    {formatCurrency(summary.fixedSubtotal)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <div className="px-4 py-2.5 border-b border-border bg-accent/40">
              <p className="font-display font-600 text-sm text-foreground">Variable Budget Estimate</p>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Line Item</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {renderRows(variableItems)}
                <TableRow className="bg-muted/40">
                  <TableCell className="text-right font-medium">Variable Subtotal</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">
                    {formatCurrency(summary.variableSubtotal)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="border-2 border-foreground">
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <p className="font-display font-700 text-lg text-foreground">Total Estimated Budget</p>
              <p className="text-xs text-muted-foreground">Excludes travel &amp; travel-related costs</p>
            </div>
            <p data-testid="shared-total" className="font-display font-700 text-2xl tabular-nums text-foreground">
              {formatCurrency(summary.total)}
            </p>
          </CardContent>
        </Card>

        {summary.hasPdf && (
          <div className="flex justify-center pt-2">
            <a href={sharedPdfUrl(token)} target="_blank" rel="noopener noreferrer" data-testid="shared-view-pdf-link">
              <Button className="gap-2">
                <FileText className="h-4 w-4" /> View full proposal PDF
              </Button>
            </a>
          </div>
        )}

        <p className="text-xs text-muted-foreground leading-relaxed">
          This is a proposed estimated budget for discussion purposes only and is subject to change based on
          information provided (or not provided) by the Client. It does not constitute a binding commitment of any kind.
        </p>
      </main>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className="text-foreground mt-0.5">{value}</p>
    </div>
  );
}
